import { Fragment } from "@tiptap/pm/model";
import { TextSelection } from "@tiptap/pm/state";
import { CellSelection, TableMap, deleteCellSelection } from "@tiptap/pm/tables";
import { findParentNodeClosestToPos } from "@tiptap/react";
import {
  absolutePositionToRelativePosition,
  relativePositionToAbsolutePosition,
  ySyncPluginKey,
} from "@tiptap/y-tiptap";

import { isMatrixCell } from "@/components/expression/utils/matrix";
import { writeSliceToClipboard } from "@/components/tiptap/copy-paste/writeSliceToClipboard";
import { isModelNodeInLockedBlock } from "@/components/tiptap/lock/utils";
import { getCellPosition, isCellLike } from "@/components/tiptap/tables/utils";
import { prepareTiptapContentCopy } from "@/components/tiptap/utils/copyTiptapContent";
import { pasteNodes } from "@/input/utils/editOperators";
import { cursorIsActive } from "@/models/CursorModel";
import { GroupNodeModel } from "@/models/nodes/GroupNodeModel";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";

import type { DocumentModel } from "@/models/DocumentModel";
import type { MatrixCell, MatrixNodeModel } from "@/models/nodes/MatrixNodeModel";
import type { Node as ProseMirrorNode, ResolvedPos, Slice } from "@tiptap/pm/model";
import type { EditorState, Transaction } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";
import type { RelativePosition } from "yjs";

export type GridAxis = "row" | "col";

export type GridAction = "insertBefore" | "insertAfter" | "remove" | "clear" | "duplicate" | "copy";

type ClipboardWriter = (view: EditorView, slice: Slice) => unknown;

/** The actions that change the grid. */
type GridEditAction = Exclude<GridAction, "copy">;

type GridShape = { row: number; col: number; rows: number; cols: number };

export type GridTarget =
  | ({ kind: "matrix" | "cases"; cell: MatrixCell } & GridShape)
  | ({ kind: "table"; cellPos: number } & GridShape);

/** What the options menu is opened for — the grid it acts on and which of its lines. */
export type GridMenuTarget = { target: GridTarget; axis: GridAxis };

/**
 * The grid line the math cursor or the text selection is in. Math wins, so a
 * matrix typed inside a table cell gets its own controls. Every parenthesis
 * group wraps a 1×1 matrix, which isn't a grid to the user unless it's a cases
 * system.
 */
export function getActiveGridTarget(doc: DocumentModel): GridTarget | null {
  return getMatrixTarget(doc) ?? getTableTarget(doc);
}

function getMatrixTarget(doc: DocumentModel): GridTarget | null {
  const { cursor } = doc;

  if (!cursorIsActive(cursor)) {
    return null;
  }

  for (const node of [cursor.node, ...cursor.node.ancestors]) {
    if (!isMatrixCell(node)) {
      continue;
    }

    const matrix = node.parent;
    const group = matrix.parent;
    const isCases = !!group?.is(GroupNodeModel) && !!group.casesSystemEnabled;
    const position = matrix.getCellPosition(node);

    if (position && (isCases || matrix.rows * matrix.cols > 1)) {
      return {
        kind: isCases ? "cases" : "matrix",
        cell: node,
        ...position,
        rows: matrix.rows,
        cols: matrix.cols,
      };
    }
  }

  return null;
}

function getTableTarget(doc: DocumentModel): GridTarget | null {
  const editor = doc.editor.tiptap;

  if (!editor?.isEditable) {
    return null;
  }

  const cell = findParentNodeClosestToPos(editor.state.selection.$head, isCellLike);

  return cell ? getTableTargetAt(editor.state, cell.pos) : null;
}

function getTableTargetAt(state: EditorState, cellPos: number): GridTarget | null {
  const cell = state.doc.nodeAt(cellPos);

  // getCellPosition logs an error for a position that isn't a cell, e.g. a deleted table's.
  if (!cell || !isCellLike(cell)) {
    return null;
  }

  const table = findParentNodeClosestToPos(
    state.doc.resolve(cellPos),
    (node) => node.type.name === "table",
  );
  const position = getCellPosition(state.doc, cellPos);

  if (!table || !position) {
    return null;
  }

  const map = TableMap.get(table.node);

  return {
    kind: "table",
    cellPos,
    row: position.row,
    col: position.col,
    rows: map.height,
    cols: map.width,
  };
}

/**
 * One target per line along `axis`, keeping the other coordinate: every row of
 * the caret's column, or every column of its row. A spanning table cell repeats.
 */
export function getGridLineTargets(
  doc: DocumentModel,
  target: GridTarget,
  axis: GridAxis,
): GridTarget[] {
  const count = axis === "row" ? target.rows : target.cols;
  const indices = Array.from({ length: count }, (_, i) => i);
  const positionAt = (i: number) =>
    axis === "row" ? { row: i, col: target.col } : { row: target.row, col: i };

  if (target.kind !== "table") {
    const matrix = target.cell.parent;
    return indices.flatMap((i) => {
      const position = positionAt(i);
      const cell = matrix.matrix[position.row]?.[position.col];
      return isMatrixCell(cell) ? [{ ...target, ...position, cell }] : [];
    });
  }

  const state = doc.editor.tiptap?.state;
  const table =
    state &&
    findParentNodeClosestToPos(
      state.doc.resolve(target.cellPos),
      (node) => node.type.name === "table",
    );

  if (!table) {
    return [];
  }

  const map = TableMap.get(table.node);
  return indices.map((i) => {
    const position = positionAt(i);
    const cellPos = table.start + map.map[position.row * map.width + position.col];
    return { ...target, ...position, cellPos };
  });
}

/**
 * Moves the target's whole line, content and all, to index `to`, keeps it
 * selected, and returns the target at its new place. A table line crossed by a
 * merged cell can't move.
 */
export function moveGridLine(
  doc: DocumentModel,
  { target, axis }: GridMenuTarget,
  to: number,
): GridTarget | null {
  const from = axis === "row" ? target.row : target.col;
  const position = axis === "row" ? { row: to, col: target.col } : { row: target.row, col: to };

  if (from === to) {
    return target;
  }

  // A drag keeps its target, so the doc may have been locked or made read-only since it began.
  if (!doc.editable) {
    return null;
  }

  if (target.kind !== "table") {
    // Math edits skip ProseMirror's lock check, which guards the table branch.
    if (isModelNodeInLockedBlock(target.cell)) {
      return null;
    }
    const matrix = target.cell.parent;
    doc.transact(() => {
      if (axis === "row") {
        matrix.moveRow(from, to);
      } else {
        matrix.moveColumn(from, to);
      }
    }, `matrix:${axis}:move`);
    const moved = { ...target, ...position };
    selectGridLine(doc, { target: moved, axis });
    return moved;
  }

  const editor = doc.editor.tiptap;
  const state = editor?.state;
  const table =
    state &&
    findParentNodeClosestToPos(
      state.doc.resolve(target.cellPos),
      (node) => node.type.name === "table",
    );

  if (!editor || !table) {
    return null;
  }

  const map = TableMap.get(table.node);
  const first = Math.min(from, to);
  const last = Math.max(from, to);
  const crossCount = axis === "row" ? map.width : map.height;
  const cellOffset = (line: number, cross: number) =>
    axis === "row" ? map.map[line * map.width + cross] : map.map[cross * map.width + line];

  const replacements: { pos: number; node: ProseMirrorNode }[] = [];

  for (let cross = 0; cross < crossCount; cross++) {
    const offsets = Array.from({ length: last - first + 1 }, (_, i) =>
      cellOffset(first + i, cross),
    );
    const cells = offsets
      .map((offset) => table.node.nodeAt(offset))
      .filter(
        (cell): cell is ProseMirrorNode => cell?.attrs.colspan === 1 && cell.attrs.rowspan === 1,
      );

    if (cells.length !== offsets.length) {
      return null;
    }

    const reordered = [...cells];
    reordered.splice(to - first, 0, ...reordered.splice(from - first, 1));
    offsets.forEach((offset, i) => {
      replacements.push({ pos: table.start + offset, node: reordered[i] });
    });
  }

  const { tr } = state;

  // A row's height is on the row, not its cells. Its size doesn't change, so the
  // cell positions below stay valid.
  if (axis === "row") {
    const rows: { pos: number; node: ProseMirrorNode }[] = [];
    table.node.forEach((row, offset) => rows.push({ pos: table.start + offset, node: row }));
    const lines = rows.slice(first, last + 1);
    const reordered = [...lines];
    reordered.splice(to - first, 0, ...reordered.splice(from - first, 1));
    lines.forEach(({ pos }, i) => tr.setNodeMarkup(pos, undefined, reordered[i].node.attrs));
  }

  // Last first, so each replacement only shifts cells that are already done.
  replacements
    .sort((a, b) => b.pos - a.pos)
    .forEach(({ pos, node }) => {
      tr.replaceWith(pos, pos + (tr.doc.nodeAt(pos)?.nodeSize ?? 0), node);
    });

  const movedTable = tr.doc.nodeAt(table.pos);

  if (!movedTable) {
    return null;
  }

  const movedMap = TableMap.get(movedTable);
  const cellPos = table.start + movedMap.map[position.row * movedMap.width + position.col];
  tr.setSelection(getTableLineSelection(tr.doc.resolve(cellPos), axis));
  editor.view.dispatch(tr);

  return { ...target, ...position, cellPos };
}

/** Puts the caret at the end of the target's cell. */
export function moveCaretToGridTarget(doc: DocumentModel, target: GridTarget) {
  if (target.kind !== "table") {
    doc.matrixSelection.resetSelection();
    doc.cursor.moveTo(target.cell, target.cell.nodes.length);
    return;
  }

  const editor = doc.editor.tiptap;
  const cell = editor?.state.doc.nodeAt(target.cellPos);

  if (editor && cell && isCellLike(cell)) {
    const end = target.cellPos + cell.nodeSize - 1;
    editor.view.dispatch(
      editor.state.tr.setSelection(TextSelection.near(editor.state.doc.resolve(end), -1)),
    );
  }
}

/** A menu target that `resolveGridMenu` can find again after collaborators' edits. */
export type AnchoredGridMenu = { menu: GridMenuTarget; cellAnchor: RelativePosition | null };

export function anchorGridMenu(doc: DocumentModel, menu: GridMenuTarget): AnchoredGridMenu {
  const state = doc.editor.tiptap?.state;
  const cellAnchor =
    menu.target.kind === "table" && state ? toRelativePosition(state, menu.target.cellPos) : null;
  return { menu, cellAnchor };
}

/**
 * The anchored menu with its cell where it is now. Collaborators' changes replace
 * the whole ProseMirror doc, so a table cell is tracked by its Yjs position
 * rather than mapped; its row, column and the grid's size are read afresh.
 */
export function resolveGridMenu(
  doc: DocumentModel,
  { menu, cellAnchor }: AnchoredGridMenu,
): GridMenuTarget | null {
  const { target } = menu;

  if (target.kind !== "table") {
    const matrix = target.cell.parent;
    const position = matrix.getCellPosition(target.cell);
    return position
      ? { ...menu, target: { ...target, ...position, rows: matrix.rows, cols: matrix.cols } }
      : null;
  }

  const state = doc.editor.tiptap?.state;

  if (!cellAnchor || !state) {
    return menu;
  }

  const cellPos = toAbsolutePosition(state, cellAnchor);
  const resolved = cellPos === null ? null : getTableTargetAt(state, cellPos);
  return resolved && { ...menu, target: resolved };
}

// The native menu is async: the target is captured at tap time and applied when
// the user picks an option, after the editor has been blurred for the sheet.
const pendingMenus = new WeakMap<DocumentModel, AnchoredGridMenu>();

export function setPendingGridMenu(doc: DocumentModel, menu: GridMenuTarget) {
  pendingMenus.set(doc, anchorGridMenu(doc, menu));
}

function takePendingGridMenu(doc: DocumentModel): GridMenuTarget | null {
  const pending = pendingMenus.get(doc);
  pendingMenus.delete(doc);

  return pending ? resolveGridMenu(doc, pending) : null;
}

type YSyncState = { doc: unknown; type: unknown; binding: { mapping: unknown } | null };

function toRelativePosition(state: EditorState, pos: number) {
  const ystate = ySyncPluginKey.getState(state) as YSyncState | undefined;
  if (!ystate?.binding) return null;
  return absolutePositionToRelativePosition(
    pos,
    ystate.type as never,
    ystate.binding.mapping as never,
  ) as RelativePosition;
}

function toAbsolutePosition(state: EditorState, relativePosition: RelativePosition) {
  const ystate = ySyncPluginKey.getState(state) as YSyncState | undefined;
  if (!ystate?.binding) return null;
  return relativePositionToAbsolutePosition(
    ystate.doc as never,
    ystate.type as never,
    relativePosition as never,
    ystate.binding.mapping as never,
  );
}

/** `writeClipboard` is where "copy" puts the line — the browser's clipboard unless given. */
export function applyPendingGridAction(
  doc: DocumentModel,
  action: GridAction,
  writeClipboard: ClipboardWriter = writeSliceToClipboard,
) {
  const menu = takePendingGridMenu(doc);

  if (!menu) {
    return;
  }

  if (action === "copy") {
    copyGridLine(doc, menu, writeClipboard);
    return;
  }

  // The doc may have been locked or made read-only while the menu was open.
  if (!doc.editable) {
    return;
  }

  if (menu.target.kind === "table") {
    applyTableAction(doc, menu.target.cellPos, menu.axis, action);
  } else {
    applyMatrixAction(doc, menu.target.cell, menu.axis, action);
  }
}

/**
 * Copies the line like Cmd+C on it: a table line as its cells, a matrix line as
 * a matrix of the selected cells.
 */
function copyGridLine(doc: DocumentModel, menu: GridMenuTarget, writeClipboard: ClipboardWriter) {
  const view = doc.editor.tiptap?.view;

  const { target } = menu;
  const isEmptySingleCell =
    target.kind !== "table" && target.rows * target.cols === 1 && !target.cell.nodes.length;

  // With nothing selected, the copy would take the whole formula.
  if (isEmptySingleCell) {
    return;
  }

  // The sheet blurred the editor, so the menu's line selection may be gone.
  selectGridLine(doc, menu);

  if (view) {
    void writeClipboard(view, view.state.selection.content());
  }
}

function applyMatrixAction(
  doc: DocumentModel,
  cell: MatrixCell,
  axis: GridAxis,
  action: GridEditAction,
) {
  const matrix = cell.parent;
  const position = matrix.getCellPosition(cell);

  if (!position || isModelNodeInLockedBlock(cell)) {
    return;
  }

  if (action === "remove" && (axis === "row" ? matrix.rows : matrix.cols) === 1) {
    removeMatrix(doc, matrix);
    return;
  }

  let { row, col } = position;
  const lineCells = () => (axis === "row" ? matrix.matrix[row] : matrix.matrix.map((r) => r[col]));

  doc.transact(() => {
    switch (action) {
      case "remove":
        if (axis === "row") {
          matrix.removeRow(row);
        } else {
          matrix.removeColumn(col);
        }
        row = Math.min(row, matrix.rows - 1);
        col = Math.min(col, matrix.cols - 1);
        break;

      case "clear":
        lineCells().forEach((c) => c.setChildren([]));
        break;

      case "duplicate": {
        const source = lineCells();
        if (axis === "row") {
          row += 1;
          matrix.insertRow(row);
        } else {
          col += 1;
          matrix.insertColumn(col);
        }
        // Same path as Cmd+D on a math selection: fresh ids, variables merged
        // with the doc's, computed results re-linked.
        lineCells().forEach((copy, i) => {
          const json = source[i].nodes.map((node) => node.toJSON());
          pasteNodes(doc, json, { ignoreActiveCursor: true }, copy);
        });
        break;
      }

      case "insertBefore":
      case "insertAfter": {
        const offset = action === "insertAfter" ? 1 : 0;
        if (axis === "row") {
          row += offset;
          matrix.insertRow(row);
        } else {
          col += offset;
          matrix.insertColumn(col);
        }
        break;
      }
    }
  }, `matrix:${axis}:${action}`);

  doc.matrixSelection.resetSelection();

  const nextCell = matrix.getCellNodeByPosition({ row, col });

  if (nextCell) {
    queueMicrotask(() => {
      doc.cursor.moveTo(nextCell, nextCell.nodes.length, { forceFocus: true });
    });
  }
}

/** Removes the matrix along with the parentheses or cases group around it. */
function removeMatrix(doc: DocumentModel, matrix: MatrixNodeModel) {
  const { parent } = matrix;
  const grid = parent?.is(GroupNodeModel) && parent.matrixNode === matrix ? parent : matrix;
  const inline = grid.parent;
  const { index } = grid;

  doc.matrixSelection.resetSelection();
  doc.transact(() => {
    doc.cursor.moveBeforeNode(grid);
    doc.removeNode(grid);
  }, "matrix:remove");

  if (inline?.is(InlineNodeModel)) {
    queueMicrotask(() => {
      doc.cursor.moveTo(inline, index, { forceFocus: true });
    });
  }
}

function applyTableAction(
  doc: DocumentModel,
  cellPos: number,
  axis: GridAxis,
  action: GridEditAction,
) {
  const editor = doc.editor.tiptap;
  const { state } = editor ?? {};
  const cell = state?.doc.nodeAt(cellPos);
  const position = state && getCellPosition(state.doc, cellPos);
  const table =
    state &&
    findParentNodeClosestToPos(state.doc.resolve(cellPos), (node) => node.type.name === "table");

  if (!editor || !cell || !isCellLike(cell) || !position || !table) {
    return;
  }

  const chain = editor.chain().command(({ tr }) => {
    tr.setSelection(TextSelection.near(tr.doc.resolve(cellPos + 1)));
    return true;
  });
  const isRow = axis === "row";
  const map = TableMap.get(table.node);

  switch (action) {
    case "insertBefore":
      if (isRow) chain.addRowBefore().goToNextCellVertical(-1);
      else chain.addColumnBefore().goToPreviousCell();
      break;
    case "insertAfter":
      if (isRow) chain.addRowAfter().goToNextCellVertical(1);
      else chain.addColumnAfter().goToNextCell();
      break;
    case "remove":
      if ((isRow ? map.height : map.width) === 1) chain.deleteTable();
      else if (isRow) chain.deleteRow();
      else chain.deleteColumn();
      break;
    case "clear":
      chain
        .command(({ tr }) => {
          tr.setSelection(getTableLineSelection(tr.doc.resolve(cellPos), axis));
          return true;
        })
        .command(({ state: chainState, dispatch }) => deleteCellSelection(chainState, dispatch))
        .command(({ tr }) => {
          tr.setSelection(TextSelection.near(tr.doc.resolve(tr.mapping.map(cellPos) + 1)));
          return true;
        });
      break;
    case "duplicate": {
      const index = isRow ? position.row : position.col;
      if (isRow) chain.addRowAfter();
      else chain.addColumnAfter();
      const lineCount = isRow ? map.height : map.width;
      chain.command(({ tr }) => copyTableLine(doc, tr, table.pos, axis, index, lineCount));
      if (isRow) chain.goToNextCellVertical(1);
      else chain.goToNextCell();
      break;
    }
  }

  chain.focus().run();
}

/**
 * Copies every cell's content of line `index` into the (just inserted) line after
 * it. Math is duplicated like a Cmd+D line copy, so the copies don't share nodes.
 * `lineCount` is the line count before the insert.
 */
function copyTableLine(
  doc: DocumentModel,
  tr: Transaction,
  tablePos: number,
  axis: GridAxis,
  index: number,
  lineCount: number,
) {
  const table = tr.doc.nodeAt(tablePos);
  const map = table && TableMap.get(table);

  // A chain runs its later commands after a failed one, so without the new line
  // this would overwrite the line that was already after `index`.
  if (!table || !map || (axis === "row" ? map.height : map.width) !== lineCount + 1) {
    return false;
  }
  const tableStart = tablePos + 1;
  const count = axis === "row" ? map.width : map.height;
  const copies: { target: number; targetCell: ProseMirrorNode; sourceCell: ProseMirrorNode }[] = [];

  for (let i = 0; i < count; i++) {
    const [source, target] =
      axis === "row"
        ? [map.map[index * map.width + i], map.map[(index + 1) * map.width + i]]
        : [map.map[i * map.width + index], map.map[i * map.width + index + 1]];
    const sourceCell = table.nodeAt(source);
    const targetCell = table.nodeAt(target);

    // A cell spanning both lines was widened instead of copied.
    if (source === target || copies.some((copy) => copy.target === target)) {
      continue;
    }
    if (sourceCell && targetCell) {
      copies.push({ target, targetCell, sourceCell });
    }
  }

  // The whole line in one copy, so a reference to a result follows its formula
  // into the copy when both are in the line. A row holds a column's cells too.
  const line = table.child(0).type.create(
    null,
    copies.map(({ sourceCell }) => sourceCell),
  );
  const copiedCells = prepareTiptapContentCopy(line, doc, doc).jsonContent?.content ?? [];

  // Backwards, so each replacement only shifts cells that are already done.
  for (let i = copies.length - 1; i >= 0; i--) {
    const { target, targetCell } = copies[i];
    tr.replaceWith(
      tableStart + target + 1,
      tableStart + target + targetCell.nodeSize - 1,
      Fragment.fromJSON(tr.doc.type.schema, copiedCells[i]?.content ?? []),
    );
  }

  return true;
}

function getTableLineSelection($cell: ResolvedPos, axis: GridAxis) {
  return axis === "row" ? CellSelection.rowSelection($cell) : CellSelection.colSelection($cell);
}

/** Highlights the line the menu will act on, so the user sees what "Delete" removes. */
export function selectGridLine(doc: DocumentModel, { target, axis }: GridMenuTarget) {
  if (target.kind === "table") {
    const editor = doc.editor.tiptap;
    const cell = editor?.state.doc.nodeAt(target.cellPos);

    if (editor && cell && isCellLike(cell)) {
      const $cell = editor.state.doc.resolve(target.cellPos);
      editor.view.dispatch(editor.state.tr.setSelection(getTableLineSelection($cell, axis)));
    }
    return;
  }

  const { cell } = target;
  const matrix = cell.parent;
  const position = matrix.getCellPosition(cell);

  if (!position) {
    return;
  }

  // The caret may sit in a group nested inside the cell.
  if (doc.cursor.node !== cell) {
    doc.cursor.moveTo(cell, cell.nodes.length);
  }

  if (doc.matrixSelection.setupFromCursor(doc.cursor)) {
    doc.matrixSelection.setSelectionRange(
      axis === "row" ? { row: position.row, col: 0 } : { row: 0, col: position.col },
      axis === "row"
        ? { row: position.row, col: matrix.cols - 1 }
        : { row: matrix.rows - 1, col: position.col },
    );
    return;
  }

  // A 1×1 cases group has no matrix selection, so its line is the cell's content.
  doc.selection.resetSelection();
  doc.selection.setInlineNodeRangeBetween(cell, 0, cell, cell.nodes.length);
}
